import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { RegistrationService, saveOnboardingEmail } from '../registration.service';

@Component({
  selector: 'app-activate-account',
  templateUrl: './activate-account.component.html',
  styleUrls: ['./activate-account.component.css'],
  standalone: false,
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  private static readonly ACTIVATION_RETRY_DELAY_MS = 1500;

  public loading = true;
  public state = '';
  public email = '';
  public tenantId = '';
  public token = '';
  public error = '';
  public resendInFlight = false;
  public passwordVisible = false;
  public confirmPasswordVisible = false;
  public activationForm: FormGroup;
  public activationInFlight = false;
  public activationPreparing = false;
  private activationRetryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: FormBuilder,
    private readonly snackbar: MatSnackBar,
    private readonly registrationService: RegistrationService,
  ) {
    this.activationForm = this.formBuilder.group(
      {
        email: [{ value: '', disabled: true }],
        password: ['', [Validators.required, this.passwordStrengthValidator()]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: [this.passwordsMatchValidator()] },
    );
  }

  ngOnInit(): void {
    this.tenantId = (
      this.route.snapshot.queryParamMap.get('tenantId')
      ?? this.route.snapshot.queryParamMap.get('tenantName')
      ?? ''
    ).trim();
    this.token = (this.route.snapshot.queryParamMap.get('token') ?? '').trim();

    if (!this.tenantId || !this.token) {
      this.loading = false;
      this.state = 'invalid';
      this.error = 'Verification link is incomplete.';
      return;
    }

    this.registrationService.verifyOnboarding(this.tenantId, this.token).subscribe({
      next: (response) => {
        this.loading = false;
        this.state = response.state;
        this.email = response.email;
        this.activationForm.patchValue({ email: response.email });
      },
      error: (err) => {
        this.loading = false;
        this.state = err?.error?.state || 'invalid';
        this.error = err?.error?.error || 'Unable to verify your email.';
      },
    });
  }

  ngOnDestroy(): void {
    this.clearActivationRetryTimer();
  }

  resendVerification(): void {
    if (!this.tenantId || this.resendInFlight) {
      return;
    }
    this.resendInFlight = true;
    this.registrationService.resendVerification(this.tenantId).subscribe({
      next: () => {
        this.resendInFlight = false;
        this.state = 'resent';
      },
      error: (err) => {
        this.resendInFlight = false;
        this.error = err?.error?.error || 'Unable to resend the verification email.';
      },
    });
  }

  submitActivation(): void {
    this.activationForm.markAllAsTouched();
    if (this.activationForm.invalid) {
      return;
    }
    if (!this.tenantId || !this.token) {
      this.state = 'invalid';
      this.error = 'Activation link is incomplete.';
      return;
    }

    this.error = '';
    this.activationInFlight = true;
    this.activationPreparing = false;
    this.clearActivationRetryTimer();
    this.activateAccount();
  }

  fieldError(controlName: 'password' | 'confirmPassword'): string {
    const control = this.activationForm.get(controlName);
    if (!control || !control.touched) {
      return '';
    }
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (controlName === 'password' && control.hasError('passwordStrength')) {
      return 'Password must meet all rules below';
    }
    return '';
  }

  passwordRules(): { label: string; met: boolean }[] {
    const value = this.activationForm.get('password')?.value ?? '';
    const confirmValue = this.activationForm.get('confirmPassword')?.value ?? '';
    return [
      { label: 'At least 12 characters', met: value.length >= 12 },
      { label: 'One uppercase letter', met: /[A-Z]/.test(value) },
      { label: 'One lowercase letter', met: /[a-z]/.test(value) },
      { label: 'One number', met: /\d/.test(value) },
      { label: 'One special character', met: /[^A-Za-z0-9]/.test(value) },
      { label: 'Passwords match', met: !!confirmValue && value === confirmValue },
    ];
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.passwordVisible = !this.passwordVisible;
      return;
    }
    this.confirmPasswordVisible = !this.confirmPasswordVisible;
  }

  private passwordsMatchValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const password = group.get('password')?.value ?? '';
      const confirmPassword = group.get('confirmPassword')?.value ?? '';
      if (!password || !confirmPassword) {
        return null;
      }
      return password === confirmPassword ? null : { passwordMismatch: true };
    };
  }

  private passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value ?? '';
      const valid = (
        value.length >= 12
        && /[A-Z]/.test(value)
        && /[a-z]/.test(value)
        && /\d/.test(value)
        && /[^A-Za-z0-9]/.test(value)
      );
      return valid ? null : { passwordStrength: true };
    };
  }

  private handleActivationError(err: { error?: { state?: string; status?: string; error?: string } }): void {
    const state = err?.error?.state || 'activation_failed';
    const status = err?.error?.status || '';

    if (state === 'used' && this.tenantId) {
      if (status === 'checkout_pending') {
        this.snackbar.open('Account already activated. Continue to Atlas checkout.', 'OK');
        this.navigateForward(this.tenantId, status, this.email);
        return;
      }

      if (status === 'checkout_started' || status === 'provisioning' || status === 'ready') {
        this.snackbar.open('Account already activated. Opening your onboarding status.', 'OK');
        this.navigateForward(this.tenantId, status, this.email);
        return;
      }
    }

    if (state === 'identity_pending') {
      this.activationPreparing = true;
      this.scheduleActivationRetry();
      return;
    }

    this.activationPreparing = false;
    this.state = state;
    this.error = err?.error?.error || 'Unable to activate your account.';
  }

  private activateAccount(): void {
    this.registrationService
      .activateOnboarding(this.tenantId, this.token, this.activationForm.get('password')?.value ?? '')
      .subscribe({
        next: (response) => {
          this.clearActivationRetryTimer();
          this.activationInFlight = false;
          this.activationPreparing = false;
          this.state = response.state;
          this.snackbar.open('Account activated. Continue to Atlas checkout.', 'OK');
          this.navigateForward(response.tenant_name, response.status, response.email || this.email);
        },
        error: (err) => {
          if (err?.error?.state !== 'identity_pending') {
            this.clearActivationRetryTimer();
            this.activationInFlight = false;
          }
          this.handleActivationError(err);
        },
      });
  }

  private scheduleActivationRetry(): void {
    this.clearActivationRetryTimer();
    this.activationRetryTimer = setTimeout(() => {
      this.activationRetryTimer = null;
      this.activateAccount();
    }, ActivateAccountComponent.ACTIVATION_RETRY_DELAY_MS);
  }

  private clearActivationRetryTimer(): void {
    if (this.activationRetryTimer !== null) {
      clearTimeout(this.activationRetryTimer);
      this.activationRetryTimer = null;
    }
  }

  private navigateForward(tenantId: string, status: string, email?: string): void {
    const normalizedEmail = email?.trim();
    saveOnboardingEmail(tenantId, normalizedEmail);
    if (status === 'checkout_pending') {
      this.router.navigate(['/atlas-signup'], {
        queryParams: {
          tenantId,
          step: 'subscription',
        },
        state: normalizedEmail ? { tenantInfo: { tenantName: tenantId, emailAddress: normalizedEmail } } : undefined,
      });
      return;
    }

    this.router.navigate(['/atlas/checkout/success'], {
      queryParams: { tenantId },
    });
  }
}
